import React, { useState, useEffect } from 'react';
import { 
  INVESTMENT_RETURNS, 
  calculateCompoundInterest, 
  suggestOptimalAllocation,
  checkGoalAchievability,
  calculateYearsToEducationStage,
  EDUCATION_COSTS
} from '../../utils/educationCalculations';
import './SavingsSimulator.css';

const SavingsSimulator = ({ 
  currentSavings, 
  monthlyBudget, 
  currentEducationSavings,
  childAge, 
  schoolType,
  onSavingsChange 
}) => {
  const [selectedInvestments, setSelectedInvestments] = useState({
    savings: { enabled: true, allocation: 20, monthlyAmount: 0 },
    nisa: { enabled: true, allocation: 40, monthlyAmount: 0 },
    gakushiInsurance: { enabled: true, allocation: 30, monthlyAmount: 0 },
    indexFund: { enabled: true, allocation: 10, monthlyAmount: 0 }
  });
  
  const [simulationResults, setSimulationResults] = useState([]);
  const [goalAchievability, setGoalAchievability] = useState([]);
  const [riskTolerance, setRiskTolerance] = useState('medium');
  const [customMonthlySavings, setCustomMonthlySavings] = useState(currentEducationSavings);

  const yearsToUniversity = calculateYearsToEducationStage(childAge, 'university');
  const totalEducationCost = Object.values(EDUCATION_COSTS[schoolType]).reduce((sum, cost) => sum + cost, 0);

  // 投資商品の日本語名
  const investmentNames = {
    savings: '普通預金',
    timeDeposit: '定期預金',
    gakushiInsurance: '学資保険',
    nisa: 'つみたてNISA',
    juniorNisa: 'ジュニアNISA',
    indexFund: 'インデックスファンド',
    balanceFund: 'バランスファンド',
    foreignStock: '外国株式ファンド',
    bondFund: '債券ファンド',
    individualStock: '個別株投資'
  };

  // 配分に基づく月額計算
  useEffect(() => {
    const totalAllocation = Object.values(selectedInvestments)
      .filter(inv => inv.enabled)
      .reduce((sum, inv) => sum + inv.allocation, 0);
    
    if (totalAllocation > 0) {
      const updatedInvestments = { ...selectedInvestments };
      Object.keys(updatedInvestments).forEach(key => {
        if (updatedInvestments[key].enabled) {
          updatedInvestments[key].monthlyAmount = Math.round(
            (customMonthlySavings * updatedInvestments[key].allocation) / totalAllocation
          );
        } else {
          updatedInvestments[key].monthlyAmount = 0;
        }
      });
      setSelectedInvestments(updatedInvestments);
    }
  }, [customMonthlySavings]);

  // シミュレーション実行
  useEffect(() => {
    const results = [];
    let totalProjected = currentSavings;
    
    Object.entries(selectedInvestments).forEach(([investment, config]) => {
      if (config.enabled && config.monthlyAmount > 0) {
        const rate = INVESTMENT_RETURNS[investment] || 0;
        const initialAmount = (currentSavings * config.allocation) / 100;
        const projected = calculateCompoundInterest(
          initialAmount,
          rate,
          yearsToUniversity,
          config.monthlyAmount
        );
        
        results.push({
          investment,
          name: investmentNames[investment],
          rate: (rate * 100).toFixed(1),
          monthlyAmount: config.monthlyAmount,
          allocation: config.allocation,
          projectedAmount: projected,
          totalContribution: initialAmount + (config.monthlyAmount * 12 * yearsToUniversity),
          profit: projected - (initialAmount + (config.monthlyAmount * 12 * yearsToUniversity))
        });
      }
    });
    
    setSimulationResults(results);
    
    // 目標達成可能性チェック
    const achievability = checkGoalAchievability(
      currentSavings,
      customMonthlySavings,
      childAge,
      totalEducationCost,
      schoolType
    );
    setGoalAchievability(achievability);
  }, [selectedInvestments, currentSavings, customMonthlySavings, childAge, schoolType, yearsToUniversity, totalEducationCost]);

  // 最適配分の適用
  const applyOptimalAllocation = () => {
    const suggestions = suggestOptimalAllocation(
      customMonthlySavings,
      currentSavings,
      childAge,
      riskTolerance
    );
    
    const newInvestments = { ...selectedInvestments };
    
    // 全てを無効化
    Object.keys(newInvestments).forEach(key => {
      newInvestments[key].enabled = false;
      newInvestments[key].allocation = 0;
    });
    
    // 提案に基づいて設定
    suggestions.forEach(suggestion => {
      if (newInvestments[suggestion.investment]) {
        newInvestments[suggestion.investment].enabled = true;
        newInvestments[suggestion.investment].allocation = Math.round(suggestion.ratio * 100);
      }
    });
    
    setSelectedInvestments(newInvestments);
  };

  const totalProjectedAmount = simulationResults.reduce((sum, result) => sum + result.projectedAmount, 0);
  const totalMonthlyAmount = simulationResults.reduce((sum, result) => sum + result.monthlyAmount, 0);
  const achievementRate = totalEducationCost > 0 ? (totalProjectedAmount / totalEducationCost) * 100 : 0;

  return (
    <div className="savings-simulator">
      <h3>貯蓄シミュレーター</h3>
      
      {/* 基本設定 */}
      <div className="simulator-settings">
        <div className="setting-row">
          <label htmlFor="monthly-savings">月間教育費貯金額</label>
          <input
            type="number"
            id="monthly-savings"
            value={customMonthlySavings}
            onChange={(e) => {
              const value = parseInt(e.target.value) || 0;
              setCustomMonthlySavings(value);
              if (onSavingsChange) onSavingsChange(value);
            }}
            min="0"
            max={monthlyBudget}
            step="1000"
          />
          <span className="unit">円</span>
          <span className="budget-info">
            （余裕資金: {monthlyBudget.toLocaleString()}円）
          </span>
        </div>
        
        <div className="setting-row">
          <label htmlFor="risk-tolerance">リスク許容度</label>
          <select
            id="risk-tolerance"
            value={riskTolerance}
            onChange={(e) => setRiskTolerance(e.target.value)}
          >
            <option value="conservative">安全重視</option>
            <option value="medium">バランス</option>
            <option value="aggressive">積極投資</option>
          </select>
          <button 
            className="optimize-button"
            onClick={applyOptimalAllocation}
          >
            最適配分を適用
          </button>
        </div>
      </div>

      {/* 投資配分設定 */}
      <div className="investment-allocation">
        <h4>投資配分設定</h4>
        <div className="allocation-grid">
          {Object.entries(selectedInvestments).map(([investment, config]) => (
            <div key={investment} className="allocation-item">
              <label className="investment-checkbox">
                <input
                  type="checkbox"
                  checked={config.enabled}
                  onChange={(e) => {
                    setSelectedInvestments(prev => ({
                      ...prev,
                      [investment]: {
                        ...prev[investment],
                        enabled: e.target.checked
                      }
                    }));
                  }}
                />
                <span className="investment-name">{investmentNames[investment]}</span>
                <span className="investment-rate">
                  ({(INVESTMENT_RETURNS[investment] * 100).toFixed(1)}%)
                </span>
              </label>
              
              {config.enabled && (
                <div className="allocation-controls">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={config.allocation}
                    onChange={(e) => {
                      setSelectedInvestments(prev => ({
                        ...prev,
                        [investment]: {
                          ...prev[investment],
                          allocation: parseInt(e.target.value)
                        }
                      }));
                    }}
                  />
                  <span className="allocation-value">{config.allocation}%</span>
                  <span className="monthly-amount">
                    {config.monthlyAmount.toLocaleString()}円/月
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* シミュレーション結果 */}
      <div className="simulation-results">
        <h4>シミュレーション結果（{yearsToUniversity}年後）</h4>
        
        <div className="results-summary">
          <div className="summary-card">
            <div className="summary-label">予想貯蓄額</div>
            <div className="summary-value primary">
              {totalProjectedAmount.toLocaleString()}円
            </div>
          </div>
          
          <div className="summary-card">
            <div className="summary-label">目標達成率</div>
            <div className={`summary-value ${achievementRate >= 100 ? 'success' : 'warning'}`}>
              {achievementRate.toFixed(1)}%
            </div>
          </div>
          
          <div className="summary-card">
            <div className="summary-label">不足額</div>
            <div className="summary-value">
              {Math.max(0, totalEducationCost - totalProjectedAmount).toLocaleString()}円
            </div>
          </div>
        </div>

        {simulationResults.length > 0 && (
          <div className="detailed-results">
            <table className="results-table">
              <thead>
                <tr>
                  <th>投資商品</th>
                  <th>月額</th>
                  <th>想定利回り</th>
                  <th>予想額</th>
                  <th>利益</th>
                </tr>
              </thead>
              <tbody>
                {simulationResults.map((result, index) => (
                  <tr key={index}>
                    <td>{result.name}</td>
                    <td>{result.monthlyAmount.toLocaleString()}円</td>
                    <td>{result.rate}%</td>
                    <td>{result.projectedAmount.toLocaleString()}円</td>
                    <td className={result.profit > 0 ? 'profit' : 'loss'}>
                      {result.profit > 0 ? '+' : ''}{result.profit.toLocaleString()}円
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 戦略別比較 */}
      <div className="strategy-comparison">
        <h4>戦略別達成可能性</h4>
        <div className="strategy-grid">
          {goalAchievability.map((strategy, index) => (
            <div key={index} className="strategy-card">
              <h5>{strategy.name}</h5>
              <p className="strategy-description">{strategy.description}</p>
              <div className="strategy-metrics">
                <div className="metric">
                  <span className="metric-label">達成率</span>
                  <span className={`metric-value ${strategy.isAchievable ? 'success' : 'warning'}`}>
                    {strategy.achievementRate}%
                  </span>
                </div>
                <div className="metric">
                  <span className="metric-label">予想額</span>
                  <span className="metric-value">
                    {strategy.projectedAmount.toLocaleString()}円
                  </span>
                </div>
                {strategy.shortfall > 0 && (
                  <div className="metric">
                    <span className="metric-label">追加必要額</span>
                    <span className="metric-value warning">
                      +{strategy.requiredMonthly.toLocaleString()}円/月
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SavingsSimulator;
